// Captures the Chromium `beforeinstallprompt` event so we can offer a native "Install" button on
// Android/desktop, and tracks the `appinstalled` signal. iOS never fires beforeinstallprompt (Apple
// exposes no install API), so there the UI falls back to written Add-to-Home-Screen steps.
//
// The listeners must be attached at bootstrap (before React mounts) — the browser fires
// beforeinstallprompt early and only once, so a late listener misses it.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function initInstallCapture(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppress Chrome's mini-infobar; we surface our own button in the install step instead.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPrompt = null;
    emit();
  });
}

/** True when a native install prompt is available (Android/desktop Chromium). */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Fire the browser's native install prompt. Resolves true if the user accepted. */
export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  emit();
  return outcome === "accepted";
}

export function wasInstalled(): boolean {
  return installed;
}

/** Subscribe to install-availability / installed changes. Returns an unsubscribe fn. */
export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
